import { apiClient } from './client'

// HikeStrong Challenges (permanent progression challenges)
export type HikeStrongChallenge = {
  id: string
  slug: string
  name: string
  description?: string | null
  location?: string | null
  order: number
  distanceKm: number
  elevationGainMeters: number
  difficulty?: string | null
  mapImageUrl?: string | null
  geoJson?: string | null
  geoJsonMetrics?: {
    distanceKm: number
    elevationGainMeters: number
  } | null
  badgeImageUrl?: string | null
  badgeId?: string | null
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export type HikeStrongChallengePayload = {
  slug?: string | null
  name: string
  description?: string | null
  location?: string | null
  order?: number | null
  distanceKm: number
  elevationGainMeters: number
  difficulty?: string | null
  mapImageUrl?: string | null
  geoJson?: string | null
  badgeImageUrl?: string | null
  badgeId?: string | null
  isActive: boolean
}

// Seasonal Challenges (time-limited events)
export type SeasonalChallenge = {
  id: string
  slug: string
  name: string
  summary?: string | null
  distanceKm: number
  elevationGainMeters: number
  difficulty?: string | null
  startAt: string
  endAt: string
  badgeImageUrl?: string | null
  badgeId?: string | null
  sponsorLogoUrl?: string | null
  sponsorText?: string | null
  isRecommended: boolean
  recommendationOrder?: number | null
  isPublished: boolean
  isArchived: boolean
  createdAt: string
  updatedAt: string
}

export type SeasonalChallengePayload = {
  slug?: string | null
  name: string
  summary?: string | null
  distanceKm: number
  elevationGainMeters: number
  difficulty?: string | null
  startAt: string
  endAt: string
  badgeImageUrl?: string | null
  badgeId?: string | null
  sponsorLogoUrl?: string | null
  sponsorText?: string | null
  isRecommended?: boolean
  recommendationOrder?: number | null
  isPublished: boolean
  isArchived?: boolean
}

export type SeasonalChallengePreviewResponse = {
  previewAt: string
  challenges: SeasonalChallenge[]
}

export type ChallengeCatalogStatus = {
  isDegraded: boolean
  skippedCount: number
  skippedActiveCount: number
  allActiveRecordsUnreadable: boolean
}

export type ChallengeCatalogIssue = {
  recordId: string
  error: string
  isActive: boolean
}

export type ChallengeCatalogResponse<T> = {
  items: T[]
  status: ChallengeCatalogStatus
  issues: ChallengeCatalogIssue[]
}

// HikeStrong Challenge API
export async function getHikeStrongChallenges(): Promise<ChallengeCatalogResponse<HikeStrongChallenge>> {
  const { data } = await apiClient.get<ChallengeCatalogResponse<HikeStrongChallenge>>('/api/admin/challenges/hikestrong')
  return data
}

export async function getHikeStrongChallengeById(id: string): Promise<HikeStrongChallenge> {
  const { data } = await apiClient.get<HikeStrongChallenge>(`/api/admin/challenges/hikestrong/${id}`)
  return data
}

export async function createHikeStrongChallenge(payload: HikeStrongChallengePayload): Promise<HikeStrongChallenge> {
  const { data } = await apiClient.post<HikeStrongChallenge>('/api/admin/challenges/hikestrong', payload)
  return data
}

export async function updateHikeStrongChallenge(id: string, payload: HikeStrongChallengePayload): Promise<HikeStrongChallenge> {
  const { data } = await apiClient.put<HikeStrongChallenge>(`/api/admin/challenges/hikestrong/${id}`, payload)
  return data
}

export type ChallengeImageUploadResponse = {
  imageUrl: string
  objectKey: string
}

export async function uploadHikeStrongBadgeImage(file: File, challengeKey?: string): Promise<ChallengeImageUploadResponse> {
  const formData = new FormData()
  formData.append('file', file)

  if (challengeKey?.trim()) {
    formData.append('challengeKey', challengeKey.trim())
  }

  const { data } = await apiClient.post<ChallengeImageUploadResponse>(
    '/api/admin/challenges/hikestrong/badge-image/upload',
    formData
  )
  return data
}

export async function uploadSeasonalBadgeImage(file: File, challengeKey?: string): Promise<ChallengeImageUploadResponse> {
  const formData = new FormData()
  formData.append('file', file)

  if (challengeKey?.trim()) {
    formData.append('challengeKey', challengeKey.trim())
  }

  const { data } = await apiClient.post<ChallengeImageUploadResponse>(
    '/api/admin/challenges/seasonal/badge-image/upload',
    formData
  )
  return data
}

export type ResetHikeStrongChallengesResult = {
  deletedChallengesCount: number
  deletedProgressCount: number
  deletedWorkoutEventCount: number
}

export type DeleteHikeStrongChallengeResult = {
  deletedChallengesCount: number
  reassignedProgressCount: number
  nextChallengeId?: string | null
  nextChallengeOrder?: number | null
}

export async function resetHikeStrongChallenges(): Promise<ResetHikeStrongChallengesResult> {
  const { data } = await apiClient.delete<ResetHikeStrongChallengesResult>('/api/admin/challenges/hikestrong/all')
  return data
}

export async function deleteHikeStrongChallenge(id: string): Promise<DeleteHikeStrongChallengeResult> {
  const { data } = await apiClient.delete<DeleteHikeStrongChallengeResult>(`/api/admin/challenges/hikestrong/${id}`)
  return data
}

// Seasonal Challenge API
export async function getSeasonalChallenges(includeArchived = false): Promise<ChallengeCatalogResponse<SeasonalChallenge>> {
  const { data } = await apiClient.get<ChallengeCatalogResponse<SeasonalChallenge>>('/api/admin/challenges/seasonal', {
    params: { includeArchived }
  })
  return data
}

export async function previewSeasonalChallenges(previewAt: string): Promise<SeasonalChallengePreviewResponse> {
  const { data } = await apiClient.get<SeasonalChallengePreviewResponse>('/api/admin/challenges/seasonal/preview', {
    params: { previewAt }
  })
  return data
}

export async function getSeasonalChallengeById(id: string): Promise<SeasonalChallenge> {
  const { data } = await apiClient.get<SeasonalChallenge>(`/api/admin/challenges/seasonal/${id}`)
  return data
}

export async function createSeasonalChallenge(payload: SeasonalChallengePayload): Promise<SeasonalChallenge> {
  const { data } = await apiClient.post<SeasonalChallenge>('/api/admin/challenges/seasonal', payload)
  return data
}

export async function updateSeasonalChallenge(id: string, payload: SeasonalChallengePayload): Promise<SeasonalChallenge> {
  const { data } = await apiClient.put<SeasonalChallenge>(`/api/admin/challenges/seasonal/${id}`, payload)
  return data
}

export async function publishSeasonalChallenge(id: string): Promise<SeasonalChallenge> {
  const { data } = await apiClient.post<SeasonalChallenge>(`/api/admin/challenges/seasonal/${id}/publish`)
  return data
}

export async function unpublishSeasonalChallenge(id: string): Promise<SeasonalChallenge> {
  const { data } = await apiClient.post<SeasonalChallenge>(`/api/admin/challenges/seasonal/${id}/unpublish`)
  return data
}

export async function archiveSeasonalChallenge(id: string, archived = true): Promise<SeasonalChallenge> {
  const { data } = await apiClient.post<SeasonalChallenge>(`/api/admin/challenges/seasonal/${id}/archive`, null, {
    params: { archived }
  })
  return data
}

// Bulk GeoJSON Upload
export type GeoJsonBulkUploadSuccess = {
  challengeId: string
  challengeSlug: string
  challengeName: string
  sourceFileName: string
  distanceKm: number
  elevationGainMeters: number
  usedExistingElevationGainFallback: boolean
}

export type GeoJsonBulkUploadFailure = {
  challengeKey: string
  sourceFileName: string
  error: string
}

export type GeoJsonBulkUploadResult = {
  totalFiles: number
  updatedCount: number
  updated: GeoJsonBulkUploadSuccess[]
  failures: GeoJsonBulkUploadFailure[]
}

export async function bulkUploadHikeStrongGeoJson(files: File[]): Promise<GeoJsonBulkUploadResult> {
  const formData = new FormData()
  for (const file of files) {
    formData.append('files', file)
  }
  const { data } = await apiClient.post<GeoJsonBulkUploadResult>(
    '/api/admin/challenges/hikestrong/geojson/bulk',
    formData
  )
  return data
}

export const DIFFICULTY_OPTIONS = ['Easy', 'Moderate', 'Hard', 'Expert'] as const
export type Difficulty = (typeof DIFFICULTY_OPTIONS)[number]
